import React, { useState, useMemo } from "react";
import { FaBuilding } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { ALL_FIRM_TAB_PERMISSIONS, ALL_DEALER_FIRM_TAB_PERMISSIONS } from "../../../../utils/firmUtils";
import { useDebounce } from "../../../../Hooks/useDebounce";
import useSubFirmActions from "../../../../Hooks/storageRoles/useSubFirmActions";
import { MascotLoadingOverlay, EmptyState, SearchInput } from "../../../shared";
import SubFirmsTable from "../SubFirmsTable";
import SubFirmsTableSkeleton from "../SubFirmsTableSkeleton";
import FirmBusyBadge from "../../FirmBusyBadge";
import QuotaSplitCards, { sumFirmsQuotaByProduct } from "../QuotaSplitCards";

/**
 * Firma > Alt Firmalar sekmesi. Bayi firmada (#376) tek "Müşteriler" sekmesi
 * olur: `customersSource` verilince bayinin müşterileri alt bayilerle AYNI
 * listeye girer; satır tipine göre (alt bayi / müşteri) rozet, bağı kopar ucu
 * ve kota kuralı seçilir. `includeSubDealers` false ise (izin yok) alt bayi
 * satırları çekilmez.
 *
 * İşlemler (bağla / kopar / kota ver-al, meşguliyet) useSubFirmActions'ta
 * (#324) — merkezde Kota & Cihazlar sayfası da aynısını kullanır. Bu sekme
 * bayi panelinde (dealerRoles/SubFirmsTab) duruyor.
 */
const SubFirmsTab = ({
  firm,
  onSelectedFirm,
  title,
  externalData,
  externalHooks,
  customersSource = null,
  includeSubDealers = true,
}) => {
  const { auth } = useAuth();
  const { hasAnyPermission } = usePermission();
  // Alt firma satırına tıklayıp detayına gitme: hedef firmanın herhangi bir
  // sekme iznine sahip olmak gerekir (FirmCard.js ile aynı kural).
  const canNavigateToSubFirm = hasAnyPermission(
    ...(auth?.role === "dealer" ? ALL_DEALER_FIRM_TAB_PERMISSIONS : ALL_FIRM_TAB_PERMISSIONS)
  );
  const [search, setSearch] = useState("");
  const displayTitle = title || "Alt Firmalar";
  const debouncedSearch = useDebounce(search, 250);

  const actions = useSubFirmActions({
    firm,
    externalData,
    externalHooks,
    customersSource,
    includeSubDealers,
  });
  const {
    isDealerFirm,
    visibleFirms,
    isLoading,
    isFetching,
    sendableQuota,
    sendableFetching,
    busyIds,
    parentBusy,
  } = actions;

  // Arama gecikmeli (#407). bayserNo sayı gelebilir — String ile karşılaştırılır.
  const filteredFirms = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return visibleFirms || [];
    return (visibleFirms || []).filter((subFirm) =>
      [subFirm.firmName, subFirm.bayserNo, subFirm.email, subFirm.tel].some(
        (v) => v != null && String(v).toLowerCase().includes(q)
      )
    );
  }, [visibleFirms, debouncedSearch]);

  // Kartların sağ yarısı alt firma verisinden — alt firmalar yenilenirken
  // kartlar da skeleton'a döner (eski toplam gösterip zıplamasın).
  const cardsLoading = sendableFetching || isLoading || isFetching;

  // BAŞLIKTAKI KOTA KARTLARI (#300) — sol: firmanın kendisinde (havuz + kendi
  // cihazları), sağ: listedeki firmaların ürün bazında toplamı.
  const subFirmTotalsByProduct = useMemo(() => sumFirmsQuotaByProduct(visibleFirms || []), [visibleFirms]);

  const productsForCards = useMemo(() => {
    const rows = sendableQuota.map((p) => ({
      productName: p.productName,
      left: p.total ?? 0,
      right: subFirmTotalsByProduct.get(p.productName) ?? 0,
    }));
    // Birleşik sekmede yalnız müşteride olan ürünler de kartta görünür
    if (customersSource) {
      const known = new Set(rows.map((r) => r.productName));
      subFirmTotalsByProduct.forEach((total, productName) => {
        if (!known.has(productName)) rows.push({ productName, left: 0, right: total });
      });
    }
    return rows;
  }, [sendableQuota, subFirmTotalsByProduct, customersSource]);

  const merged = !!customersSource;

  return (
    <div className="flex flex-col h-full w-full">
      {actions.renderLayer()}

      {/* Sticky Header — Cihazlar sekmesiyle aynı iskelet: Satır 1 başlık +
          arama + aksiyon, Satır 2 ürün kota kartları. */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 shrink-0 px-2 sm:px-4 py-3 sm:py-4 space-y-2.5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
            <FaBuilding className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="shrink-0">
            <h2 className="text-base sm:text-lg font-Bold text-gray-800 flex items-center gap-2">
              {displayTitle}
              {parentBusy && <FirmBusyBadge />}
            </h2>
            <p className="text-xs text-gray-400">
              {isLoading ? "Yükleniyor..." : `${filteredFirms.length} firma kayıtlı`}
            </p>
          </div>

          <SearchInput
            placeholder="Firma adı, bayser no, email ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="flex-1 min-w-0 ml-auto max-w-md"
          />

          {/* Alt Firma Ekle — arama + onay (useSubFirmActions) */}
          {actions.renderLinkButton()}
        </div>

        {/* Satır 2: Ürün Kota Kartları — sol: bu firmanın kendisinde (havuz +
            kendi cihazları), dik çizginin sağında: listedeki firmalarda.
            "Dağıtılan" dili yok (#300). */}
        <QuotaSplitCards
          items={productsForCards}
          loading={cardsLoading}
          leftLabel={isDealerFirm ? "Bayide" : "Firmada"}
          rightLabel={merged ? "Müşterilerde" : isDealerFirm ? "Alt Bayilerde" : "Alt Firmalarda"}
        />
      </div>

      {/* İçerik — KENAR BOŞLUKSUZ (full-bleed). Cihazlar sekmesiyle aynı:
          tablo kendi kabuğunda kayar, yatay taşma tablonun içinde kalır. */}
      {isLoading ? (
        /* Kural: MascotLoadingOverlay her zaman skeleton'ın ÜSTÜNDE
           (bkz. docs/rules/ui-components.md). */
        <div className="relative flex-1 min-h-0">
          <MascotLoadingOverlay message="Firmalar yükleniyor..." />
          <SubFirmsTableSkeleton rows={6} />
        </div>
      ) : filteredFirms.length === 0 ? (
        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4">
          <EmptyState
            message={
              merged
                ? "Müşteri Bulunamadı"
                : displayTitle === "Alt Bayiler"
                  ? "Alt Bayi Bulunamadı"
                  : "Alt Firma Bulunamadı"
            }
            suggestion={
              search
                ? "Arama kriterlerinizi değiştirmeyi deneyin."
                : merged
                  ? "Bu bayinin altında henüz alt bayi ya da müşteri yok."
                  : displayTitle === "Alt Bayiler"
                    ? "Bu bayinin altında henüz alt bayi eklenmemiş."
                    : "Bu firmaya henüz alt firma eklenmemiş."
            }
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <SubFirmsTable
            firms={filteredFirms}
            canNavigate={canNavigateToSubFirm}
            canSendQuota={actions.canSendAny}
            canSendQuotaFor={actions.canSendQuotaFor}
            canUnlink={actions.canUnlinkAny}
            canUnlinkFor={actions.canUnlinkFor}
            showTypeBadge={merged}
            onSendQuota={actions.openSendQuota}
            onTakeBackQuota={actions.openTakeBackQuota}
            onUnlink={actions.askUnlink}
            busyIds={busyIds}
          />
        </div>
      )}

    </div>
  );
};

export default SubFirmsTab;
